import React, { useState } from 'react';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import '../css/postPage.css';
import '../css/createPost.css';
import { useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import RichTextEditor from './RichTextEditor';
import api from "../api";


function CreatePost({ onPostCreated }) {
    const [content, setContent] = useState('');
    const [media, setMedia] = useState(null);
    const [videoUrl, setVideoUrl] = useState('');
    const maxDescriptionLength = 10000;
    const [fileName, setFileName] = useState('');
    const { t } = useTranslation();
    const { isAuthenticated } = useContext(AuthContext);

    const validateUrl = (url) => {
        const regex = new RegExp(
            /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be|twitch\.tv)\/.+$/i
        );
        return regex.test(url);
    };

    const extractTextFromHtml = (html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        return doc.body.textContent || '';
    };

    const quillModules = {};
    const quillFormats = [];

    const handlePostSubmit = () => {
        const textContent = extractTextFromHtml(content).trim();
        const textLength = textContent.length;

        if (textLength === 0 || textLength < 5) {
            Swal.fire({
                title: t('posts.error'),
                text: t('posts.descriptionTooShort'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#00ff00',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
            return;
        }

        if (textLength > maxDescriptionLength) {
            Swal.fire({
                title: t('posts.error'),
                text: t('posts.descriptionTooLong', { max: maxDescriptionLength }),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#00ff00',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
            return;
        }

        if (videoUrl && !validateUrl(videoUrl)) {
            Swal.fire({
                title: t('posts.error'),
                text: t('posts.invalidUrl'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#00ff00',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
            return;
        }

        if (!media && !videoUrl) {
            Swal.fire({
                title: t('posts.error'),
                text: t('posts.addMedia'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#00ff00',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('posts.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
            return;
        }

        const accessToken = localStorage.getItem('access_token');
        if (!accessToken) {
            return;
        }

        const formData = new FormData();
        formData.append('content', content);
        if (media) {
            formData.append('media', media);
        }
        if (videoUrl) {
            formData.append('video_url', videoUrl);
        }

        api.post(`${process.env.REACT_APP_API_URL}/api/posts`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                'Authorization': `Bearer ${accessToken}`
            },
        })
            .then(() => {
                Swal.fire({
                    title: t('posts.success'),
                    text: t('posts.postCreated'),
                    icon: 'success',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonText: t('posts.ok'),
                    confirmButtonColor: '#3085d6',
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                }).then(() => {
                    setContent('');
                    setMedia(null);
                    setVideoUrl('');
                    onPostCreated();
                });
            })
            .catch(error => {
                if (error.response && error.response.data.error === 'Post limit reached for today') {
                    Swal.fire({
                        title: t('posts.error'),
                        text: t('posts.postLimitReached'),
                        icon: 'error',
                        background: '#1a1a1a',
                        color: '#b3b3b3',
                        iconColor: '#ff0000',
                        confirmButtonText: t('posts.ok'),
                        confirmButtonColor: '#d33',
                        customClass: {
                            popup: 'swal2-dark-popup',
                            confirmButton: 'swal2-dark-button'
                        }
                    });
                }
            });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setMedia(file);
            setFileName(file.name);
        }
    };

    if (!isAuthenticated) {
        return null;
    }

    return (
        <>
            <div className="posts-page">
                <h1>{t('posts.createNewPost')}</h1>
                <img src={'images/sp-line.png'} alt={'Sp_line'} id="line"/>
                <div className="create-post-wrapper">
                    <div className="post-card no-top-border">
                        <div className="create-post">
                            <div className="create-field">
                                <RichTextEditor
                                    value={content}
                                    onChange={(value) => setContent(value)}
                                />
                            </div>

                            <div className="create-field create-field-row">
                                <label className="custom-file-upload">
                                    <input type="file" onChange={handleFileChange}/>
                                    {t('posts.upload')}
                                </label>
                                {fileName && <span className="file-name" title={fileName}>{fileName}</span>}
                            </div>

                            <div className="create-field">
                                <span className="create-field-label">{t('posts.videoUrl')}</span>
                                <input
                                    type="text"
                                    value={videoUrl}
                                    onChange={(e) => setVideoUrl(e.target.value)}
                                    placeholder={t('posts.videoUrl')}
                                />
                            </div>

                            <div className="create-actions">
                                <button className="create-post-submit" onClick={handlePostSubmit}>
                                    {t('posts.createPost')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default CreatePost;
